import React, { FormEvent, FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Form, InputGroup, Modal, Overlay, OverlayTrigger, Popover, Spinner, Table, Tooltip } from "react-bootstrap";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import {
  Student,
  StudentInfo,
  TeacherInfo
} from "../../../../../store/onboarding/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faSearch, faUser } from "@fortawesome/free-solid-svg-icons";
import ModalCloseButton from "../../third-step/group-students/common/ModalCloseButton";
import { allowAlphaNumbers, escapeSpecialCharacter, getFullName } from "../../../../../utils/NamesUtils";
import LoadingIndicator from "../../LoadingIndicator";
import { CustomCheckbox } from "../evidence-tab/conduct-interview/CustomCheckbox";
import {
  addStudents,
  assignStudentsToTeacher,
  clearAddStudentsError,
  getStudents,
  hideAssignStudentsToEmployeeModal,
  openTeacherDetailsModal,
} from "../../../../../store/onboarding/actions";
import { toastr } from "react-redux-toastr";
import BootstrapTable, { SortOrder, TableChangeState, TableChangeType } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

type StateProps = {
  showModal: boolean;
  studentsRoster: Array<StudentInfo>;
  selectedEmployee?: TeacherInfo;
  selectedTeacher?: TeacherInfo;
  teachersStudents: Array<Student>;
  isLoading: {
    getStudents: boolean;
    addStudents: boolean;
    assignedStudentsToTeacher: boolean;
  };
  errors: {
    getStudents?: string;
    addStudents?: string | Array<any>;
    assignStudentsToTeacher?: string;
  };
};

type DispatchProps = {
  openTeacherDetailsModal: () => any;
  hideAssignEducatorsToCoachModal: () => any;
  getStudents: (includeClasses?: boolean) => any;
  addStudents: (
      students: Array<Student>,
      addStudentsToEvidence?: boolean
    ) => any;
  clearAddStudentsError: () =>  any;
  assignStudentsToTeacher: (
    teacherId: number,
    students: Array<PartialStudentInfo>
  ) => any;
  };
  
type Props = StateProps & DispatchProps;

interface PartialStudentInfo {
 first_name: string; 
 last_name: string; 
 unique_id: string; 
}

const AssignStudentsToEmployeeModal: FunctionComponent<Props> = ({
  showModal,
  isLoading,
  errors,
  studentsRoster,
  selectedEmployee,
  selectedTeacher,
  teachersStudents,
  assignStudentsToTeacher,
  openTeacherDetailsModal,
  getStudents,
  hideAssignEducatorsToCoachModal,
  addStudents,
}) => {
  const [studentsFilter, setStudentsFilter] = useState("");

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(undefined);

  const [selectedStudentIDs, setSelectedStudentIDs] = useState<Array<string>>([]);

  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  const [optimisticStudents, setOptimisticStudents] = useState<PartialStudentInfo[]>([]);

  const addRef = useRef(null);

  const handleAddClick = () => {
    setShowOverlay((prevState) => !prevState);
  };

  const onHideAddOverlay = () => {
    setShowOverlay(false);
  };

  const [newStudent, setNewStudent] = useState<Student>({
      first_name: "",
      last_name: "",
      unique_id: "",
    });

  useEffect(() => {
    if(showModal){
      if (!(studentsRoster.length || isLoading.getStudents)) {
        getStudents();
      }
      setNewStudent({
        first_name: "",
        last_name: "",
        unique_id: "",
      });
      setStudentsFilter("");
      setSelectedStudentIDs([]);
      onHideAddOverlay();
    }
    
  },[showModal])

   const columns = useMemo(
      () => [
        {
          dataField: 'selectCheckbox',
          text: 'SELECT',
          headerStyle: { width: '20%' },
        },
        {
          dataField: 'fullName',
          text: 'STUDENT NAME',
          sort: true,
          headerStyle: { width: '50%' },
        },
        {
          dataField: 'uniqueId',
          text: 'STUDENT ID',
          sort: true,
          headerStyle: { width: '20%' },
        },  
      ],
      []
    );
  
    const options = useMemo(
      () => ({
        sizePerPageList: [
          {
            text: "10",
            value: 10,
          },
          {
            text: "20",
            value: 20,
          },
          {
            text: "30",
            value: 30,
          },
        ],
      }),
      []
    );
  
    const defaultSorted: [{ dataField: string; order: SortOrder }] = useMemo(
      () => [
        {
          dataField: "fullName",
          order: "asc",
        },
      ],
      []
    );

  const unAssignedStudentsRoster: Array<PartialStudentInfo> = useMemo(() => {
    const teacherStudentIds = new Set(
      teachersStudents.map(s => s.unique_id)
    );

    const baseList = studentsRoster
    .filter(std => !teacherStudentIds.has(std.unique_id))
    .map(s => ({
      first_name: s.first_name,
      last_name: s.last_name,
      unique_id: s.unique_id!
    }));

    const seen = new Set<string>();
    return [...optimisticStudents, ...baseList].filter(s => {
      if (seen.has(s.unique_id)) return false;
      seen.add(s.unique_id);
      return true;
    });
  }, [studentsRoster, teachersStudents, optimisticStudents]);

  useEffect(() => {
    if (!isLoading.assignedStudentsToTeacher) {
      if (errors.assignStudentsToTeacher) {
        toastr.error("Failed", errors.assignStudentsToTeacher);
      }
    }
  }, [isLoading.assignedStudentsToTeacher, errors.assignStudentsToTeacher]);

  const selectedStudents: PartialStudentInfo[] = useMemo(() => 
    unAssignedStudentsRoster.filter((s) =>
      selectedStudentIDs.includes(s.unique_id!)
    ), [selectedStudentIDs, unAssignedStudentsRoster]);

  const unAssignedStudentsRosterRows = useMemo(() => {
    return unAssignedStudentsRoster
    .filter((student) => {
      const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
      return (
        fullName.includes(studentsFilter.toLowerCase()) ||
        student.unique_id?.toLowerCase().includes(studentsFilter.toLowerCase())
      );
    })
    .map((student, index) => {
    return  {
      selectCheckbox: (
        <Form.Check
            label
            custom
            className="observedItemCheckbox"
            type="checkbox"
            onChange={() => handleAssignStudentChange(student.unique_id!)}
            checked={selectedStudentIDs.includes(''+student.unique_id!)}
          />
      ),
      fullName: getFullName(student),
      uniqueId: student.unique_id,
    }});
  }, [unAssignedStudentsRoster, studentsFilter, selectedStudentIDs]);

  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
    []
  );

  const sortedUnAssignedStudentsRosterRows = useMemo(() => {
    const originalData = [...unAssignedStudentsRosterRows];
    if (!sortField || !sortOrder) {
      return originalData;
    }

    const sortedData = [...originalData].sort((a: any, b: any) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      const isEmpty = (val: any) => val === null || val === undefined || val === "";
      if (isEmpty(aValue) && !isEmpty(bValue)) return 1;
      if (!isEmpty(aValue) && isEmpty(bValue)) return -1;
      if (isEmpty(aValue) && isEmpty(bValue)) return 0;

      aValue = aValue?.toString().trim();
      bValue = bValue?.toString().trim();

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? collator.compare(aValue, bValue)
          : collator.compare(bValue, aValue);
      }
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sortedData;
  }, [unAssignedStudentsRosterRows, sortField, sortOrder, collator]);

  const handleTableChange = (
    type: TableChangeType,
    { sortField: clickedField }: TableChangeState<any>
  ) => {
    if (type === "sort") {
      let newSortField: string | undefined = clickedField;
      let newSortOrder: "asc" | "desc" | undefined = "asc";

      if (sortField === clickedField) {
        if (sortOrder === "asc") {
          newSortOrder = "desc";
        } else if (sortOrder === "desc") {
          newSortField = undefined;
          newSortOrder = undefined;
        }
      }

      setSortField(newSortField);
      setSortOrder(newSortOrder);
    }
  };

  useEffect(() => {
    const classListDesc = document.getElementsByClassName('caret-4-desc') as HTMLCollectionOf<HTMLElement>;
    const classListAsc = document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>;

    const setDisplay = (elements: HTMLCollectionOf<HTMLElement>, display: string) => {
      Array.from(elements).forEach((el) => {
        el.style.display = display;
      });
    };

    if (sortOrder === 'asc') {
      setDisplay(classListAsc, 'inline-block');
      setDisplay(classListDesc, 'none');
    } else if (sortOrder === 'desc') {
      setDisplay(classListDesc, 'inline-block');
      setDisplay(classListAsc, 'none');
    } else if (!sortField && !sortOrder) {
      setDisplay(classListAsc, 'none');
      setDisplay(classListDesc, 'none');
    }
  }, [sortField, sortOrder]);



  const handleChange = (event: FormEvent<any>) => {
      const target = event.target as HTMLInputElement;
       let sanitizedValue = target.value;
      
      // Restrict first/last name fields
        if (target.name === "first_name" || target.name === "last_name") {
          sanitizedValue = escapeSpecialCharacter(target.value);
        }
    
      // Restrict student ID field
        if (target.name === "unique_id") {
          sanitizedValue = allowAlphaNumbers(target.value);
        }
      setNewStudent((prevStudent) => ({
        ...prevStudent,
        [target.name]: sanitizedValue,
      }));
    };

  const handleShowModal = () => {
    setStudentsFilter("");
    setSelectedStudentIDs([]);
  };

  const handleHideModal = () => {
    hideAssignEducatorsToCoachModal();
    openTeacherDetailsModal();
  };

  const onStudentFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { value } = e.target;
    setStudentsFilter(value);
  };

  const handleStudentChangesAccept = (event: React.FormEvent<any>) => {
    event.preventDefault();
    if (selectedTeacher) {
      const selectedStudents: PartialStudentInfo[] = unAssignedStudentsRoster.filter((s) =>
        selectedStudentIDs.includes(s.unique_id!)
      );
        assignStudentsToTeacher(selectedTeacher.user.id, selectedStudents)
        .then(() => {
          toastr.success("Success", "Student(s) assigned successfully.");
          handleHideModal();
        })
        .catch((error: string) => {
          toastr.error("Failed to assign student(s)", error);
        });
    }
  };

  const handleAssignStudentChange = (studentUniqueId: string) => {
    setSelectedStudentIDs((selectedStudentIDs: string[]) =>
      selectedStudentIDs.includes(studentUniqueId)
        ? selectedStudentIDs.filter((id) => id !== studentUniqueId)
        : [...selectedStudentIDs, studentUniqueId]
    );
  };

  const handleAddNewStudentSubmit = (event: FormEvent) => {
    event.preventDefault();
    if(studentsRoster.some((s) => s.unique_id === newStudent.unique_id)){
      toastr.error("Failed", "This Student ID already exists in this district.");
      return;
    }
    let newStudentInfo: PartialStudentInfo = {
      first_name: newStudent.first_name!,
      last_name: newStudent.last_name!,
      unique_id: newStudent.unique_id!,
    };
     addStudents([newStudentInfo], false).then(() => {
      toastr.success("Success", `Student added successfully! The student has been selected for 
        assignment to this teacher and has also been added to the district.`);
      setOptimisticStudents(prev =>
        prev.some(s => s.unique_id === newStudentInfo.unique_id)
          ? prev
          : [newStudentInfo, ...prev]
      );
      setSelectedStudentIDs((selectedStudentIDs: string[]) =>
        selectedStudentIDs.includes(newStudentInfo.unique_id)
          ? selectedStudentIDs.filter((id) => id !== newStudentInfo.unique_id)
          : [newStudentInfo.unique_id, ...selectedStudentIDs]
      );
      onHideAddOverlay();
      setNewStudent({
        first_name: "",
        last_name: "",
        unique_id: "",
      });
     }).catch((error:string) => {
       toastr.error("Error", error);
     });
  };

  const noDataIndicator = useMemo(() => {
    if (!unAssignedStudentsRosterRows.length && isLoading.getStudents) {
      return <LoadingIndicator type="table" rows={20} columns={3} />;
    }
    return "No matching student(s)";
  }, [isLoading.getStudents, unAssignedStudentsRosterRows]);

  return (
    <Modal
      animation={false}
      centered
      enforceFocus={false}
      backdropClassName="customDarkModalBackdrop inner in"
      show={showModal}
      onShow={handleShowModal}
      onHide={handleHideModal}
      size="lg"
      dialogClassName="modal-dialog-no-scroll"
    >
      <Modal.Header className="purpleModalHeader">
        <Modal.Title>{`Assign Student(s) to ${selectedEmployee?.user.first_name} ${selectedEmployee?.user.last_name}`}</Modal.Title>
        <ModalCloseButton onClose={handleHideModal} />
      </Modal.Header>
      <Modal.Body>
        <h2 className="font-weight-bold">
          Assign one or more students to this teacher.{" "}
        </h2>
        <div className="mb-3 mt-4 row justify-content-between align-items-end">
          <div className="col d-flex align-items-end">
            <InputGroup className="filterSearchDesktop mr-2" style={{width:"300px"}}>
              <InputGroup.Prepend>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                type="text"
                placeholder={`Search`}
                value={studentsFilter || ""}
                onChange={onStudentFilterChange}
                className="filterSearchHeight"
              />
            </InputGroup>
            {selectedStudentIDs.length
            ?
              <span className="ml-2">
                {selectedStudentIDs.length} Student(s) Selected
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Popover id="tooltip">
                      {selectedStudentIDs.length < 10
                      ?
                        <ul className="p-2 m-0 list-unstyled">
                          {selectedStudents.map((s) => <li key={s.unique_id}>{getFullName(s)}</li>) || ""}
                        </ul>
                      :
                        selectedStudents.map((s) => getFullName(s)).join(", ") || ""
                      }
                    </Popover>
                  }
                >
                  <span className="selectedStudentsCount">
                    <FontAwesomeIcon icon={faUser} className="ml-2 pointer rotate-180" />
                  </span>
                </OverlayTrigger>
              </span>
            : ''
            }
          </div>
          <div className="col-auto">
            <OverlayTrigger
              placement="top"
              trigger={["hover", "focus"]}
              overlay={
                 (showOverlay 
                  ? <div />
                  :
                    <Tooltip className="customTooltip" id="tooltip-top">
                      Add new student
                    </Tooltip>)
              }
            >
              <button
                className="blueBtnSm pointer"
                ref={addRef}
                onClick={handleAddClick}
                disabled={isLoading.getStudents}
              >
              <FontAwesomeIcon style={{color: "white"}} icon={faPlusCircle} className="mr-1" />
              New Student
            </button>
            </OverlayTrigger>
            <Overlay
              target={addRef.current}
              show={showOverlay}
              placement="left"
              rootClose
              rootCloseEvent={"click"}
              onHide={onHideAddOverlay}
              flip
            >
              <Popover id="addStudentsPopover" style={{ minWidth: "320px" }}>
                <Popover.Title>Add a new student to the district.</Popover.Title>
                <Popover.Content>
                  <Form onSubmit={handleAddNewStudentSubmit}>
                    <Form.Group>
                      <Form.Label htmlFor="first_name" className="font-weight-bold text-left">First Name<span className="label-asterisk">*</span></Form.Label>
                      <Form.Control
                        required
                        placeholder="Enter"
                        id="first_name"
                        name="first_name"
                        onChange={handleChange}
                        value={newStudent.first_name}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label htmlFor="last_name" className="font-weight-bold text-left">Last Name<span className="label-asterisk">*</span></Form.Label>
                      <Form.Control
                        required
                        placeholder="Enter"
                        id="last_name"
                        name="last_name"
                        onChange={handleChange}
                        value={newStudent.last_name}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label htmlFor="unique_id" className="font-weight-bold text-left">Student ID<span className="label-asterisk">*</span></Form.Label>
                      <Form.Control
                        required
                        placeholder="Enter"
                        id="unique_id"
                        name="unique_id"
                        onChange={handleChange}
                        value={newStudent.unique_id}
                      />
                    </Form.Group>
                    <button className="blueBtnSm w-100" type="submit" disabled={isLoading.addStudents || !newStudent.first_name || !newStudent.last_name || !newStudent.unique_id}>
                      ADD & SELECT{" "}
                      {(isLoading.addStudents) && (
                        <Spinner animation="border" size="sm" />
                      )}
                    </button>
                  </Form>
                </Popover.Content>
              </Popover>
            </Overlay>
          </div>
        </div>
        <hr/>
        <div>
          <BootstrapTable
              remote={{ sort: true }}
              classes="rosterTable"
              wrapperClasses="table-responsive mb-3 sticky-table-header"  
              keyField="id"
              bordered={false}
              bootstrap4
              data={sortedUnAssignedStudentsRosterRows}
              columns={columns}
              pagination={sortedUnAssignedStudentsRosterRows.length > 10 ? paginationFactory(options) : undefined}
              noDataIndication={noDataIndicator}
              onTableChange={handleTableChange}
              sort={sortField && sortOrder ? {
                dataField: sortField,
                order: sortOrder,
              } : undefined }
            />






          {/* <Table className="goalTable" bordered>
            <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
              <tr>
                <th style={{width:"20px"}}>SELECT</th>
                <th>STUDENTS</th>
                <th>STUDENT ID</th>
              </tr>
            </thead>
            <tbody>
              {isLoading.getStudents && !unAssignedStudentsRoster.length ? (
                <tr>
                  <td colSpan={3}>
                    <LoadingIndicator />
                  </td>
                </tr>
              ) : unAssignedStudentsRoster.length ? (
                unAssignedStudentsRosterRows
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">No matching student(s)</td>
                </tr>
              )}
            </tbody>
          </Table> */}
        </div>
        <hr />
        <div className="modalActions">
          <button className="whiteBtnSm" onClick={handleHideModal}>
            Back
          </button>
          <button
            className="blueBtnSm"
            disabled={!selectedStudentIDs.length || isLoading.assignedStudentsToTeacher}
            onClick={handleStudentChangesAccept}
          >
            ASSIGN STUDENT(S){" "}
            {(isLoading.assignedStudentsToTeacher) && (
              <Spinner animation="border" size="sm" />
            )}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = ({ onboarding }: ApplicationState): StateProps => {
  const selectedTeacher = onboarding.selectedTeacher;
    let assignStudentsToTeacherError;
    if (selectedTeacher) {
      const error = onboarding.errors.assignStudentsToTeacher.find(
        (x) => x.teacherId === selectedTeacher.user.id
      );
      if (error) {
        assignStudentsToTeacherError = error.message;
      }
    }
  return {
    showModal: onboarding.modalsState.assignStudentsToEmployee,
    selectedEmployee: onboarding.selectedTeacher,
    studentsRoster: onboarding.studentsRoster,
    selectedTeacher: selectedTeacher,
    teachersStudents: onboarding.teachersStudents,
    isLoading: {
      getStudents: onboarding.isLoading.getStudents,
      addStudents: onboarding.isLoading.addStudents,
      assignedStudentsToTeacher:
      (selectedTeacher
        ? onboarding.isLoading.assignStudentsToTeacher.includes(
            selectedTeacher.user.id
          )
        : false) || onboarding.isLoading.updateStudentDetails,
    },
    errors: {
      getStudents: onboarding.errors.getStudents,
      addStudents: onboarding.errors.addStudents,
      assignStudentsToTeacher: assignStudentsToTeacherError
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return bindActionCreators(
    {
      openTeacherDetailsModal: openTeacherDetailsModal,
      hideAssignEducatorsToCoachModal: hideAssignStudentsToEmployeeModal,
      getStudents: getStudents,
      addStudents: addStudents,
      clearAddStudentsError: clearAddStudentsError,
      assignStudentsToTeacher: assignStudentsToTeacher,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AssignStudentsToEmployeeModal);
